import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./AppManager.scss";
import { VersionPageProps, VersionType } from "../../../types/app/versionTypes";
import { useEffect, useState } from "react";
import { AlertMsg } from "../../../shared/components/alert-msg";
import { LoadingItem } from "../../../shared/components/loading";

const validationSchema = Yup.object({
    platform: Yup.string().required("Platform is required"),
    buildNumber: Yup.number()
        .typeError("Build number must be a number")
        .integer("Build number must be an integer")
        .positive("Build number must be greater than 0")
        .required("Build number is required"),

    minimumSupportedVersion: Yup.string()
        .matches(
            /^\d+(\.\d+)*$/,
            "Minimum Version must be numbers separated by dots (e.g. 1.0.3)"
        )
        .required("Minimum version is required"),
    version: Yup.string()
        .matches(
            /^\d+(\.\d+)*$/,
            "Version must be numbers separated by dots (e.g. 1.0.3)"
        )
        .required("version is required"),
    releaseDate: Yup.string().required("Release date is required"),
    effectiveDate: Yup.string().required("Effective date is required"),
    releaseNotes: Yup.string().required("Release notes are required"),
    updateUrl: Yup.string().url("Invalid URL").required("Update URL is required"),
});

interface ActionProps {
    pageProps: VersionPageProps;
    payload: VersionType | "CLEAR"
}


const createVersionAction = async ({ pageProps, payload }: ActionProps) => {
    await pageProps.createVersionAction(payload)
}

const modules = {
    toolbar: [
        [{ size: [] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }],
        [{ align: [] }],
        [{ list: "ordered" }, { list: "bullet" }],
        ["image", "link"],
    ],
};

const formats = [
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "align",
    "list",
    "bullet",
    "image",
    "link",
];

const AppManager = ({ pageProps }: { pageProps: VersionPageProps }) => {
    const [isLoading, setIsLoading] = useState(false);
    useEffect(() => {
        createVersionAction({
            pageProps,
            payload: "CLEAR"
        })
    }, [])
    const handleCreateVersion = (values: VersionType) => {
        setIsLoading(true)
        const payload: VersionType = {
            platform: values.platform,
            version: values.version,
            buildNumber: values.buildNumber,
            isCritical: values.isCritical,
            forceUpdate: values.forceUpdate,
            minimumSupportedVersion: values.minimumSupportedVersion,
            updateUrl: values.updateUrl,
            releaseNotes: values.releaseNotes,
            releaseDate: new Date(values.releaseDate).toISOString(),
            effectiveDate: new Date(values.effectiveDate).toISOString()
        }

        createVersionAction({ pageProps, payload }).finally(()=>setIsLoading(false));
    };

    return (
        <div className="app-manager">
            <h2 className="title">App Configuration</h2>
            <br />
            {pageProps.createVersionRequest.request_status === "CREATE_VERSION_FAILURE" && <AlertMsg type="error" message={pageProps.createVersionRequest.request_data.error} />}
            {pageProps.createVersionRequest.request_status === "CREATE_VERSION_SUCCESS" && <AlertMsg type="success" message={'Created Successfully'} />}
            {isLoading && <LoadingItem />}
            <Formik
                initialValues={{
                    platform: "",
                    buildNumber: 0,
                    minimumSupportedVersion: "",
                    releaseDate: "",
                    effectiveDate: "",
                    forceUpdate: false,
                    isCritical: false,
                    releaseNotes: "",
                    updateUrl: "",
                    version: "",
                }}
                validationSchema={validationSchema}
                onSubmit={handleCreateVersion}
            >
                {() => (
                    <Form className="form-grid">
                        {/* Platform */}
                        <div className="form-group">
                            <label>Platform</label>
                            <Field as="select" name="platform">
                                <option value="">Select platform</option>
                                <option value="ios">iOS</option>
                                <option value="android">Android</option>
                            </Field>
                            <ErrorMessage name="platform" component="span" className="error" />
                        </div>

                        {/* Build Number */}
                        <div className="form-group">
                            <label>Build Number</label>
                            <Field name="buildNumber" type="number" placeholder="e.g. 102" />
                            <ErrorMessage name="buildNumber" component="span" className="error" />
                        </div>

                        {/* Minimum Version */}
                        <div className="form-group">
                            <label>Minimum Supported Version</label>
                            <Field name="minimumSupportedVersion" placeholder="e.g. 101.9.0" />
                            <ErrorMessage name="minimumSupportedVersion" component="span" className="error" />
                        </div>

                        {/* Release Date */}
                        <div className="form-group">
                            <label>Release Date</label>
                            <Field
                                type="datetime-local"
                                name="releaseDate"
                            />
                            <ErrorMessage
                                name="releaseDate"
                                component="span"
                                className="error"
                            />
                        </div>

                        {/* Effective Date */}
                        <div className="form-group">
                            <label>Effective Date</label>
                            <Field
                                type="datetime-local"
                                name="effectiveDate"
                            />
                            <ErrorMessage
                                name="effectiveDate"
                                component="span"
                                className="error"
                            />
                        </div>

                        <div className="toggle-group">
                            <div className="toggle-wrapper">
                                <span className="toggle-label">Force Update</span>
                                <Field name="forceUpdate">
                                    {({ field, form }: any) => (

                                        <button
                                            type="button"
                                            className={`toggle ${field.value ? "active" : ""}`}
                                            onClick={() => form.setFieldValue(field.name, !field.value)}
                                            aria-pressed={field.value}
                                        >
                                            <span className="toggle-thumb">
                                                <svg
                                                    width="30"
                                                    height="33"
                                                    viewBox="0 0 30 33"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <g filter="url(#filter0_d)">
                                                        <rect
                                                            x="4"
                                                            y="4"
                                                            width="21.0843"
                                                            height="24.3902"
                                                            rx="10.5422"
                                                            fill="white"
                                                        />
                                                        <path
                                                            d="M19.0742 12.9297L12.8867 18.9297L10.0742 16.2024"
                                                            stroke={`${field.value ? "#00A759" : "black"}`}
                                                            strokeWidth="1.3"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </g>
                                                </svg>
                                            </span>
                                        </button>
                                    )}
                                </Field>
                            </div>
                            <div className="toggle-wrapper">
                                <span className="toggle-label">Critical Update</span>
                                <Field name="isCritical">
                                    {({ field, form }: any) => (

                                        <button
                                            type="button"
                                            className={`toggle ${field.value ? "active" : ""}`}
                                            onClick={() => form.setFieldValue(field.name, !field.value)}
                                            aria-pressed={field.value}
                                        >
                                            <span className="toggle-thumb">
                                                <svg
                                                    width="30"
                                                    height="33"
                                                    viewBox="0 0 30 33"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <g filter="url(#filter0_d)">
                                                        <rect
                                                            x="4"
                                                            y="4"
                                                            width="21.0843"
                                                            height="24.3902"
                                                            rx="10.5422"
                                                            fill="white"
                                                        />
                                                        <path
                                                            d="M19.0742 12.9297L12.8867 18.9297L10.0742 16.2024"
                                                            stroke={`${field.value ? "#00A759" : "black"}`}
                                                            strokeWidth="1.3"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </g>
                                                </svg>
                                            </span>
                                        </button>
                                    )}
                                </Field>
                            </div>
                        </div>


                        {/* Update URL */}
                        <div className="form-group">
                            <label>Update URL</label>
                            <Field
                                name="updateUrl"
                                placeholder="Please play store update url here"
                            />
                            <ErrorMessage name="updateUrl" component="span" className="error" />
                        </div>

                        {/* Version */}
                        <div className="form-group">
                            <label>Version</label>
                            <Field
                                name="version"
                                placeholder="e.g. 101.9.0"
                            />
                            <ErrorMessage name="version" component="span" className="error" />
                        </div>
                        {/* Release Notes */}
                        <div className="form-group full-width">
                            <label>Release Notes</label>

                            <Field name="releaseNotes">
                                {({ field, form }: any) => (
                                    <ReactQuill
                                        value={field.value}
                                        onChange={(val) =>
                                            form.setFieldValue(field.name, val)
                                        }
                                        onBlur={(_, __, quill) => {
                                            const plainText = quill.getText().trim();
                                            if (plainText.length === 0)
                                                form.setFieldValue(field.name, "");
                                            form.setFieldTouched(field.name, true);
                                        }}
                                        modules={modules}
                                        formats={formats}
                                        className="custom-quill"
                                    />
                                )}
                            </Field>
                            <ErrorMessage name="releaseNotes" component="span" className="error" />
                        </div>


                        <button type="submit" className="submit-btn" disabled={isLoading}>
                            Publish Update
                        </button>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default AppManager;
